import { useRef, useState } from "react";

import { NullOr, UnPromisify } from "@regal-voice/shared-types";
import camelCase from "lodash/camelCase";
import isEmpty from "lodash/isEmpty";
import snakeCase from "lodash/snakeCase";
import useDeepCompareEffect from "use-deep-compare-effect";

import { TAB_ID, TAB_ID_HEADER } from "App/main/Utils";
import { deepCaseConversion, waitForBackoffAndJitter } from "Services/HelpersService";
import { apiHeaders, type ApiHeaders } from "Services/http/apiHeaders";
import { getLogger } from "Services/LoggingService";
import { fetch } from "Services/util/fetchPonyfill";

const logger = getLogger("AbstractApiService");

export type { ApiHeaders };
export { apiHeaders };

type ServerConfigParams = {
    host: string;
    performCaseConversion?: boolean;
    performResponseCaseConversion?: boolean;
};

type RequestParams = {
    endpoint: string;
    errors: { notFound: string; other: string };
    body?: Record<string, any>;
    headers?: ApiHeaders;
    method?: "PATCH" | "POST" | "PUT" | "DELETE";
    skipDefaultErrorHandling?: boolean;
    signal?: AbortSignal;
    retryAttempts?: number;
    currentRetry?: number;
    performCaseConversion?: boolean;
    performResponseCaseConversion?: boolean;
    credentials?: RequestCredentials;
};

export type ApiRequestParams = {
    endpoint: string;
    body?: Record<string, unknown> | FormData;
    headers?: ApiHeaders;
    method?: "PATCH" | "POST" | "PUT" | "DELETE";
    signal?: AbortSignal;
    retryAttempts?: number;
    currentRetry?: number;
    performCaseConversion?: boolean;
    performResponseCaseConversion?: boolean;
    credentials?: RequestCredentials;
};

/**
 * Legacy REST client factory. Maps HTTP failures to caller-provided string messages.
 *
 * @deprecated Prefer {@link createUiRestClient} / {@link UiRestClient.asHostRestApi} for new REST endpoints.
 */
export function configureServer({
    host,
    performCaseConversion: serverRequestCaseConversion = false,
    performResponseCaseConversion: serverResponseCaseConversion = true,
}: ServerConfigParams): <T>(requestParams: RequestParams) => Promise<T> {
    return async function makeRequest<T>({
        endpoint,
        errors,
        body,
        headers,
        method,
        skipDefaultErrorHandling,
        signal,
        retryAttempts,
        currentRetry = 0,
        performCaseConversion = serverRequestCaseConversion,
        performResponseCaseConversion = serverResponseCaseConversion,
        credentials,
    }: RequestParams): Promise<T> {
        let params: {
            headers: Record<string, string>;
            method?: string;
            body?: string | FormData;
            signal?: AbortSignal;
            credentials: RequestCredentials;
        } = {
            headers: {
                ...(body instanceof FormData ? {} : { "Content-Type": "application/json" }),
                ...(headers ? headers : apiHeaders()),
                [TAB_ID_HEADER]: TAB_ID,
            },
            ...(signal ? { signal } : {}),
            credentials: "same-origin",
        };
        // How FormData: https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects
        if (!isEmpty(body) || !isEmpty(method)) {
            params = {
                ...params,
                method: method ?? "POST",
                body:
                    body instanceof FormData
                        ? body
                        : JSON.stringify(performCaseConversion ? deepCaseConversion(body, snakeCase) : body),
            };
        }

        return fetch(`${host}${endpoint}`, params)
            .then((response: Response) => {
                if (response.ok) {
                    switch (headers?.["Content-Type"]) {
                        case "text/csv":
                            return response;
                        default:
                            return response.json();
                    }
                }

                if (response.status == 400 || skipDefaultErrorHandling) {
                    return response.json().then((e) => {
                        throw e;
                    });
                } else if (response.status == 404) {
                    throw errors.notFound;
                }

                throw errors.other;
            })
            .then((res) => {
                if (performResponseCaseConversion) {
                    // do conversion back to camelCase
                    return deepCaseConversion(res, camelCase);
                }
                return res;
            })
            .catch(async (error) => {
                if (error.name == "AbortError") {
                    return; // no need to log something we are purposefully handling
                }
                const isRequestError = error.message?.indexOf("Failed to fetch") >= 0;

                // TODO: we need to investigate all the requestError types and decide
                // based on a map of error types, what the log level should be.
                logger.warn(`Fetch Error for endpoint`, { endpoint, requestError: error, isRequestError });
                if (isRequestError && retryAttempts && currentRetry < retryAttempts) {
                    await waitForBackoffAndJitter(currentRetry);
                    return makeRequest({
                        endpoint,
                        errors,
                        body,
                        headers,
                        method,
                        skipDefaultErrorHandling,
                        signal,
                        retryAttempts,
                        currentRetry: currentRetry + 1,
                        credentials,
                    });
                }
                throw error;
            });
    };
}

export function useApi<T extends (...args: any[]) => Promise<any>>(
    fn: T,
    ...args: Parameters<T>
): [NullOr<UnPromisify<ReturnType<T>>>, NullOr<string>, boolean, (...argOverrides: Parameters<T> | []) => void] {
    const [response, setResponse] = useState<NullOr<UnPromisify<ReturnType<T>>>>(null);
    const [error, setError] = useState<NullOr<string>>(null);
    const [loading, setLoading] = useState(false);

    const fetch = (...argOverrides: Parameters<T> | []) => {
        setResponse(null);
        setError(null);
        setLoading(true);

        const argsForFetch = (argOverrides.length ? argOverrides : args || []) as Parameters<T>;

        fn(...argsForFetch)
            .then(setResponse)
            .catch(setError)
            .finally(() => setLoading(false));
    };

    // WARNING: USE WITH CAUTION (copy this as well) - if considering `useDeepCompareEffect`, confirm that other approaches cannot solve the issue
    // eg, key lists or array length.  deep compare effect is very computationally heavy and relatively error prone, should only
    // be used as a last resort
    useDeepCompareEffect(() => {
        fetch();
        // could eventually return an AbortController here to kill all fetches when naving away
    }, [args]);

    return [response, error, loading, fetch];
}

export function useApiWithAbort<T extends (...args: any[]) => Promise<any>>(
    fn: T,
    ...args: Parameters<T>
): [NullOr<UnPromisify<ReturnType<T>>>, NullOr<string>, boolean, (...argOverrides: Parameters<T> | []) => void] {
    const [response, setResponse] = useState<NullOr<UnPromisify<ReturnType<T>>>>(null);
    const [error, setError] = useState<NullOr<string>>(null);
    const [loading, setLoading] = useState(false);

    const abortController = useRef<AbortController>();

    const fetch = () => {
        setResponse(null);
        setError(null);
        setLoading(true);

        abortController.current = new AbortController();
        const { signal } = abortController.current;
        fn(...[...(args || []), ...[signal]])
            .then(setResponse)
            .catch(setError)
            .finally(() => setLoading(false));
    };

    // WARNING: USE WITH CAUTION (copy this as well) - if considering `useDeepCompareEffect`, confirm that other approaches cannot solve the issue
    // eg, key lists or array length.  deep compare effect is very computationally heavy and relatively error prone, should only
    // be used as a last resort
    useDeepCompareEffect(() => {
        if (abortController.current) {
            abortController.current?.abort();
        }

        fetch();
        // could eventually return an AbortController here to kill all fetches when naving away
    }, [args]);

    return [response, error, loading, fetch];
}
